import objectScan from 'object-scan'
import { pickDeep } from '@js-utility/object'

/**
 * 序列化转换工具
 */

/**
 * 将 camelCase 字符串转换为 snake_case
 * @deprecated 上报数据已统一使用 camelCase，此函数仅供外部特殊场景使用
 */
export function camelToSnakeCase(str: string): string {
  return str
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '')
}

/**
 * 递归将对象的所有 key 从 camelCase 转换为 snake_case
 * @deprecated 上报数据已统一使用 camelCase，此函数仅供外部特殊场景使用
 */
export function toSnakeCaseKeys<T>(obj: T): unknown {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => toSnakeCaseKeys(item))
  }

  if (typeof obj === 'object' && !(obj instanceof Date)) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      const snakeKey = camelToSnakeCase(key)
      result[snakeKey] = toSnakeCaseKeys(value)
    }
    return result
  }

  return obj
}

/**
 * 将上报数据序列化为 JSON 字符串
 * 直接使用 camelCase 格式，不做字段名转换
 */
export function serializeTrackData(data: unknown): string {
  return JSON.stringify(data)
}

// ============================================
// JSON Path 工具方法
// ============================================

/**
 * 生成路径的选项
 */
export interface GeneratePathOptions {
  /** 是否只返回叶子节点路径，默认为 true */
  includeLeavesOnly?: boolean
}

/**
 * 从对象生成所有可能的路径（带通配符 [*]）
 * @param obj 元数据对象（可以是示例数据或空结构）
 * @param options 配置选项
 * @returns 路径数组
 * @example
 * const metaData = { users: [{ name: '', age: 0 }] }
 * generateWildcardPaths(metaData)
 * // 返回: ['users[*].name', 'users[*].age']
 */
export function generateWildcardPaths<T extends Record<string, unknown>>(
  obj: T,
  options: GeneratePathOptions = {}
): string[] {
  const { includeLeavesOnly = true } = options

  // 空对象检查
  if (obj === null || typeof obj !== 'object' || Object.keys(obj).length === 0) {
    return []
  }

  // 1. 使用 object-scan 获取所有具体路径（如 'users[0].name'）
  // object-scan 默认会安全处理循环引用
  const rawPaths: string[] = objectScan(['**'], {
    joined: true,
  })(obj)

  // 2. 将具体路径转换为通配符路径（将 [数字] 替换为 [*]）
  const wildcardPaths = rawPaths.map((p) => p.replace(/\[\d+\]/g, '[*]'))

  // 3. 去重（多个数组元素可能产生相同通配符路径）
  const uniquePaths = [...new Set(wildcardPaths)]

  // 4. 可选：只保留叶子节点（即路径终点不是对象/数组）
  if (includeLeavesOnly) {
    // 叶子节点判断：如果一个路径是另一个路径的前缀，则认为是中间节点
    const isPrefix = (p: string, others: string[]) =>
      others.some((o) => o !== p && (o.startsWith(p + '.') || o.startsWith(p + '[')))
    return uniquePaths.filter((p) => !isPrefix(p, uniquePaths))
  }

  return uniquePaths
}

/**
 * 根据路径列表从源对象提取子对象
 * @param source 实际业务数据源
 * @param paths 路径数组（支持通配符 [*]）
 * @returns 提取后的子对象
 * @example
 * const data = { users: [{ name: 'Alice', age: 30 }] }
 * extractByPaths(data, ['users[*].name'])
 * // 返回: { users: [{ name: 'Alice' }] }
 */
export function extractByPaths<T extends Record<string, unknown>>(
  source: T,
  paths: string[]
): Record<string, unknown> {
  // 空检查
  if (source === null || typeof source !== 'object') {
    return {}
  }

  if (!paths || paths.length === 0) {
    return {}
  }

  // 使用 pickDeep 提取数据
  return pickDeep(source, paths) as Record<string, unknown>
}


// const e = extractByPaths([{ name: 1 }], ['[*].name'])
// console.log(e)
